import { useEffect, useRef, useState } from "react";
import {
  Alert, Button, Group, Loader, Stack, Text, TextInput, UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconChevronRight, IconPrinter, IconSparkles,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../Candidates/CompanyLogo";
import { clusterProjectStatusReasons, getProjectStatus, updateProjectStatusReasonGroups } from "../api";
import { useEnrichmentCostBasis } from "../hooks/useEnrichmentCostBasis";
import { formatCandidateName } from "../_Shared/candidateName";
import { useStore } from "../store";

const REJECTION_KEYS = new Set(["consultant", "candidate", "client", "none"]);

function splitLabel(t, key) {
  if (key === "active") return t("status_still_on_search");
  if (key === "placed") return t("stage_placed");
  if (key === "onHold") return t("stage_onHold");
  return t(`rejection_${key}`, { defaultValue: key });
}

export default function ProjectStatusTab({ projectId, onOpenPerson }) {
  const { t } = useTranslation("Veft");
  const costBasis = useEnrichmentCostBasis();
  const nameFormat = useStore((s) => s.nameFormat);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clustering, setClustering] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [openCompany, setOpenCompany] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getProjectStatus(projectId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {
        if (!cancelled) notifications.show({ color: "red", title: t("error"), message: t("status_load_failed") });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, t]);

  useEffect(() => {
    const generating = data?.reasonGroupsStatus === "generating";
    if (!generating) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const next = await getProjectStatus(projectId);
        setData(next);
        if (next.reasonGroupsStatus !== "generating") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (next.reasonGroupsStatus === "complete") {
            notifications.show({ message: t("status_grouped"), color: "green", autoClose: 2000 });
          } else if (next.reasonGroupsStatus === "failed") {
            notifications.show({ color: "red", title: t("error"), message: next.reasonGroupsError || t("status_group_failed") });
          }
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [data?.reasonGroupsStatus, projectId, t]);

  async function handleCluster() {
    setClustering(true);
    try {
      const next = await clusterProjectStatusReasons(projectId);
      setData(next);
    } catch (e) {
      if (e?.status !== 402) {
        notifications.show({ color: "red", title: t("error"), message: t("status_group_failed") });
      }
    } finally {
      setClustering(false);
    }
  }

  async function saveLabel(group) {
    const label = editingLabel.trim();
    setEditingId(null);
    if (!label || label === group.label) return;
    try {
      const next = await updateProjectStatusReasonGroups(projectId, {
        groups: [{ id: group.id, label }],
      });
      setData(next);
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("status_label_save_failed") });
    }
  }

  function handlePrint() {
    const prev = document.title;
    const bits = [data?.roleTitle, data?.clientName].filter(Boolean);
    if (bits.length) document.title = bits.join(" · ");
    const restore = () => {
      document.title = prev;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  }

  if (loading || !data) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  const generating = data.reasonGroupsStatus === "generating" || clustering;
  const credits = costBasis?.statusReasonCredits;
  const showGroups = (data.reasonGroups ?? []).length > 0;
  const buckets = data.rejectionBuckets ?? [];
  const reasonSections = buckets.filter((b) => REJECTION_KEYS.has(b.key) && b.count > 0);

  return (
    <div className="search-status-sheet">
      <Group className="search-status-toolbar" justify="flex-end" mb="md" gap="xs">
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          leftSection={<IconPrinter size={14} />}
          onClick={handlePrint}
        >
          {t("status_print")}
        </Button>
      </Group>

      <div className="search-status-print-only search-status-print-title">{t("tab_status")}</div>
      <div className="search-status-kicker">
        {[data.roleTitle, data.clientName].filter(Boolean).join(" · ")}
      </div>
      <div className="search-status-lead">
        {t("status_people", { count: data.peopleCount })}
        <span className="search-status-lead-dot"> · </span>
        {t("status_companies", { count: data.companyCount })}
      </div>
      <Text className="search-status-screen-only" size="sm" c="dimmed" mt={6} mb="xl">
        {t("status_subtitle")}
      </Text>

      {buckets.length > 0 && (
        <div className="search-status-split">
          {buckets.map((b) => (
            <div key={b.key} className="search-status-split-item">
              <span className="search-status-split-count" data-tabular>{b.count}</span>
              <span className="search-status-split-label">
                {splitLabel(t, b.key)}
              </span>
            </div>
          ))}
        </div>
      )}

      {data.peopleCount === 0 ? (
        <Text size="sm" c="dimmed">{t("status_empty")}</Text>
      ) : (
        <Stack gap="xl">
          <section>
            <div className="search-status-section-label">{t("status_companies_heading")}</div>
            {data.companies.length === 0 ? (
              <Text size="sm" c="dimmed">{t("status_no_companies")}</Text>
            ) : (
              <div>
                {data.companies.map((co) => {
                  const key = co.id || co.name;
                  const expanded = openCompany === key;
                  const people = co.people ?? [];
                  return (
                    <div key={key}>
                      <UnstyledButton
                        className="search-status-company-row"
                        onClick={() => setOpenCompany(expanded ? null : key)}
                        aria-expanded={expanded}
                      >
                        <span className="search-status-expand-hint" aria-hidden>
                          <IconChevronRight
                            size={14}
                            style={{
                              transform: expanded ? "rotate(90deg)" : "none",
                              transition: "transform 0.12s ease",
                            }}
                          />
                        </span>
                        <CompanyLogo companyId={co.id} companyName={co.name} size={28} />
                        <Text size="sm" fw={500}>{co.name}</Text>
                        <span className="search-status-company-count" data-tabular>
                          {co.peopleCount}
                        </span>
                      </UnstyledButton>
                      {expanded && people.length > 0 && (
                        <div className="search-status-people">
                          {people.map((p) => (
                            <UnstyledButton
                              key={p.id}
                              className="search-status-person-row"
                              style={{ cursor: onOpenPerson ? "pointer" : "default" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenPerson?.(p.id);
                              }}
                            >
                              <Text size="sm" fw={500}>
                                {formatCandidateName(p, nameFormat) || p.name}
                              </Text>
                              {(p.title || p.employerLabel) && (
                                <Text size="xs" c="dimmed">
                                  {[p.title, p.employerLabel].filter(Boolean).join(" · ")}
                                </Text>
                              )}
                              {p.notes && (
                                <Text size="xs" c="dimmed" fs="italic" lineClamp={2}>
                                  {p.notes}
                                </Text>
                              )}
                              {p.stage && (
                                <Text size="xs" c="dimmed">
                                  {t(`stage_${p.stage}`, { defaultValue: p.stage })}
                                </Text>
                              )}
                            </UnstyledButton>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {data.unknownCurrentEmployerCount > 0 && (
              <Text className="search-status-screen-only" size="xs" c="dimmed" mt="sm">
                {t("status_unknown_employer", { count: data.unknownCurrentEmployerCount })}
              </Text>
            )}
          </section>

          <section>
            <div className="search-status-section-label">{t("status_reasons_heading")}</div>

            {reasonSections.length === 0 && data.rejectedCount === 0 && (
              <Text size="sm" c="dimmed">{t("status_no_rejected")}</Text>
            )}

            <Stack gap="lg">
              {reasonSections.map((b) => (
                <div key={b.key} className="search-status-bucket">
                  <div className="search-status-bucket-head">
                    <span className="search-status-bucket-label">
                      {splitLabel(t, b.key)}
                    </span>
                    <span className="search-status-reason-count" data-tabular>{b.count}</span>
                  </div>
                  {(b.reasons ?? []).map((g) => (
                    <div key={`${b.key}:${g.label}`} className="search-status-reason-row">
                      <span className="search-status-reason-count" data-tabular>{g.count}</span>
                      <span className="search-status-reason-label">{g.label}</span>
                    </div>
                  ))}
                </div>
              ))}
            </Stack>

            <div className="search-status-screen-only">
              {data.reasonGroupsStale && (
                <Alert
                  className="search-status-toolbar"
                  variant="light"
                  color="yellow"
                  icon={<IconAlertTriangle size={16} />}
                  mb="sm"
                  mt="lg"
                  title={t("status_stale_title")}
                >
                  <Text size="sm">{t("status_stale_body")}</Text>
                </Alert>
              )}

              {data.reasonGroupsStatus === "failed" && data.reasonGroupsError && (
                <Alert color="red" variant="light" mb="sm" mt="md" className="search-status-toolbar">
                  {data.reasonGroupsError}
                </Alert>
              )}

              {generating && (
                <Group gap="sm" py="sm">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">{t("status_grouping")}</Text>
                </Group>
              )}

              {showGroups && (
                <Stack gap={6} mt="lg">
                  <div className="search-status-section-label">{t("status_themes_heading")}</div>
                  {data.reasonGroups.map((g) => (
                    <div key={g.id} className="search-status-reason-row">
                      <span className="search-status-reason-count" data-tabular>{g.count}</span>
                      <div>
                        {editingId === g.id ? (
                          <TextInput
                            size="sm"
                            autoFocus
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.currentTarget.value)}
                            onBlur={() => saveLabel(g)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.currentTarget.blur();
                              } else if (e.key === "Escape") {
                                setEditingId(null);
                              }
                            }}
                            className="search-status-toolbar"
                          />
                        ) : (
                          <UnstyledButton
                            className="search-status-reason-label"
                            onClick={() => {
                              setEditingId(g.id);
                              setEditingLabel(g.label);
                            }}
                          >
                            {g.label}
                          </UnstyledButton>
                        )}
                        {g.summary && (
                          <Text size="xs" c="dimmed" mt={2}>{g.summary}</Text>
                        )}
                      </div>
                    </div>
                  ))}
                </Stack>
              )}

              <Group className="search-status-toolbar" mt="md">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconSparkles size={14} />}
                  onClick={handleCluster}
                  loading={generating}
                  disabled={data.rejectedCount === 0}
                >
                  {data.reasonGroupsNeedsLlm
                    ? t("status_group_for_client")
                    : t("status_prepare_reasons")}
                </Button>
                {data.reasonGroupsNeedsLlm && credits != null && (
                  <Text size="xs" c="dimmed">
                    {t("status_group_credits", { credits })}
                  </Text>
                )}
              </Group>
            </div>
          </section>
        </Stack>
      )}
    </div>
  );
}
